import { useLazyQuery, useMutation } from '@apollo/client';
import {
    BOOK_ZOHO_APPOINTMENT,
    GET_ZOHO_AVAILABILITY,
    GET_ZOHO_BOOKING_BY_JOB_APPLICATION,
    GET_ZOHO_SERVICES,
    GET_ZOHO_STAFF,
    RESCHEDULE_ZOHO_APPOINTMENT,
} from '@gql/zoho-booking';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormLabel,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    ToggleButton,
    Typography,
    styled,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { useUpdateApplicationPremiumStatus } from '@modules/jobApplications/hooks/useUpdateApplicationPremiumStatus';
import React, { useCallback, useMemo, useState } from 'react';

interface PremiumBookingDialogProps {
    open: boolean;
    onClose: () => void;
    jobApplication: any;
    premiumStatus: string;
    premiumSubStatus: string;
    onComplete: () => void;
    mode?: 'book' | 'reschedule';
}

const SlotsGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(92px, 1fr))',
    gap: theme.spacing(1),
}));

const SlotButton = styled(ToggleButton, { shouldForwardProp: p => p !== 'isSelected' })<{ isSelected?: boolean }>(
    ({ theme, isSelected }) => ({
        padding: theme.spacing(1, 0.5),
        fontSize: 14,
        fontWeight: isSelected ? 700 : 500,
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
        backgroundColor: isSelected ? theme.palette.primary.main : 'transparent',
        color: isSelected ? theme.palette.primary.contrastText : theme.palette.text.primary,
        transition: 'all 120ms ease',
        textTransform: 'none',
    }),
);

const parseSlotTime = (slot: string): string => {
    const parts = slot.split(' ')[0].split(':');
    let h = Number(parts[0]);
    const m = parts[1];
    const period = slot.match(/\b(AM|PM)\b/i);
    if (period?.[0].toUpperCase() === 'PM' && h < 12) h += 12;
    else if (period?.[0].toUpperCase() === 'AM' && h === 12) h = 0;
    return `${String(h).padStart(2, '0')}:${m}`;
};

const slotToMinutes = (slot: string): number | null => {
    const time = parseSlotTime(slot);
    const [h, m] = time.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
};

const normalizeSlots = (slots: string[]): string[] =>
    slots
        .filter(s => (slotToMinutes(s) ?? 0) % 30 === 0)
        .sort((a, b) => (slotToMinutes(a) ?? 0) - (slotToMinutes(b) ?? 0));

const groupSlotsByPeriod = (slots: string[]): { label: string; slots: string[] }[] => {
    const morning: string[] = [];
    const afternoon: string[] = [];
    slots.forEach(s => {
        const mins = slotToMinutes(s);
        if (mins !== null && mins < 12 * 60) morning.push(s);
        else if (mins !== null) afternoon.push(s);
    });
    const groups: { label: string; slots: string[] }[] = [];
    if (morning.length) groups.push({ label: 'Matin', slots: morning });
    if (afternoon.length) groups.push({ label: 'Après-midi', slots: afternoon });
    return groups;
};

const PremiumBookingDialog: React.FC<PremiumBookingDialogProps> = ({
    open,
    onClose,
    jobApplication,
    premiumStatus,
    premiumSubStatus,
    onComplete,
    mode = 'book',
}) => {
    const [selectedServiceId, setSelectedServiceId] = useState<string>('');
    const [selectedStaffId, setSelectedStaffId] = useState<string>('');
    const [selectedDate, setSelectedDate] = useState<any>(null);
    const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
    const [slots, setSlots] = useState<string[]>([]);
    const [timeZone, setTimeZone] = useState('Europe/Paris');
    const [bookingError, setBookingError] = useState<string | null>(null);
    const [existingBookingId, setExistingBookingId] = useState<string | null>(null);

    const [fetchServices, { data: servicesData, loading: servicesLoading }] = useLazyQuery(GET_ZOHO_SERVICES);
    const [fetchStaff, { data: staffData, loading: staffLoading }] = useLazyQuery(GET_ZOHO_STAFF);
    const [fetchAvailability, { loading: availLoading }] = useLazyQuery(GET_ZOHO_AVAILABILITY, {
        fetchPolicy: 'no-cache',
    });
    const [bookAppointment, { loading: bookingLoading }] = useMutation(BOOK_ZOHO_APPOINTMENT);
    const [rescheduleAppointment, { loading: rescheduleLoading }] = useMutation(RESCHEDULE_ZOHO_APPOINTMENT);
    const { updatePremiumStatus, isUpdating: statusLoading } = useUpdateApplicationPremiumStatus();

    const [fetchBooking, { loading: fetchBookingLoading }] = useLazyQuery(GET_ZOHO_BOOKING_BY_JOB_APPLICATION, {
        onCompleted: data => {
            const record = data?.getZohoBookingByJobApplication;
            if (!record?.zohoBookingId) {
                setBookingError('Aucune réservation existante à reprogrammer.');
                return;
            }
            setExistingBookingId(record.zohoBookingId);
            setSelectedServiceId(record.serviceId);
            setSelectedStaffId(record.staffId || '');
            if (record.serviceId) {
                fetchServices({ variables: { serviceId: record.serviceId } });
                fetchStaff({ variables: { serviceId: record.serviceId } });
            }
        },
        onError: () => {
            setBookingError('Impossible de récupérer la réservation existante.');
        },
    });

    const services = servicesData?.getZohoServices ?? [];
    const staffList = staffData?.getZohoStaff ?? [];
    const isLoading =
        servicesLoading ||
        staffLoading ||
        availLoading ||
        bookingLoading ||
        rescheduleLoading ||
        fetchBookingLoading ||
        statusLoading;

    const handleEnter = useCallback(() => {
        setSelectedServiceId('');
        setSelectedStaffId('');
        setSelectedDate(null);
        setSelectedSlot(null);
        setSlots([]);
        setBookingError(null);
        setExistingBookingId(null);
        if (mode === 'reschedule') {
            fetchBooking({ variables: { jobApplicationId: String(jobApplication._id) } });
        } else {
            fetchServices({ variables: {} });
        }
    }, [fetchServices, fetchBooking, mode, jobApplication._id]);

    const handleServiceChange = (serviceId: string) => {
        setSelectedServiceId(serviceId);
        setSelectedStaffId('');
        setSelectedDate(null);
        setSelectedSlot(null);
        setSlots([]);
        if (serviceId) {
            fetchStaff({ variables: { serviceId } });
        }
    };

    const handleDateChange = useCallback(
        async (date: any) => {
            if (!date || !selectedServiceId || !selectedStaffId) return;
            setSelectedDate(date);
            setSelectedSlot(null);
            setSlots([]);
            setBookingError(null);
            const dateStr = date.format('YYYY-MM-DD');
            try {
                const result = await fetchAvailability({
                    variables: {
                        input: {
                            service_id: selectedServiceId,
                            staff_id: selectedStaffId,
                            selected_date: dateStr,
                        },
                    },
                });
                const avail = result.data?.getZohoAvailability;
                if (avail?.time_zone) setTimeZone(avail.time_zone);
                setSlots(normalizeSlots(avail?.slots ?? []));
            } catch {
                setSlots([]);
            }
        },
        [selectedServiceId, selectedStaffId, fetchAvailability],
    );

    const handleConfirm = async () => {
        if (!selectedServiceId || !selectedStaffId || !selectedDate || !selectedSlot) return;
        if (mode === 'reschedule' && !existingBookingId) return;
        setBookingError(null);

        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const m = selectedDate;
        const day = String(m.date()).padStart(2, '0');
        const monthName = months[m.month()];
        const yyyy = m.year();
        const fromTime = `${day}-${monthName}-${yyyy} ${parseSlotTime(selectedSlot)}:00`;

        try {
            let appointment: any;

            if (mode === 'reschedule') {
                const result = await rescheduleAppointment({
                    variables: {
                        input: {
                            booking_id: existingBookingId,
                            staff_id: selectedStaffId,
                            start_time: fromTime,
                        },
                    },
                });
                appointment = result.data?.rescheduleZohoAppointment;
            } else {
                const result = await bookAppointment({
                    variables: {
                        input: {
                            service_id: selectedServiceId,
                            staff_id: selectedStaffId,
                            from_time: fromTime,
                            timezone: timeZone,
                        },
                        jobApplicationId: String(jobApplication._id),
                    },
                });
                appointment = result.data?.bookZohoAppointment;
            }

            if (!appointment?.booking_id) {
                setBookingError('Échec de la réservation. Veuillez réessayer.');
                return;
            }

            await updatePremiumStatus(
                String(jobApplication._id),
                premiumStatus,
                premiumSubStatus,
                appointment.start_time,
            );

            onComplete();
        } catch (err: any) {
            setBookingError(err?.message || 'Erreur lors de la réservation.');
        }
    };

    const groupedSlots = useMemo(() => groupSlotsByPeriod(slots), [slots]);
    const hasSlots = slots.length > 0;

    return (
        <Dialog
            open={open}
            onClose={isLoading ? undefined : onClose}
            TransitionProps={{ onEnter: handleEnter }}
            maxWidth="md"
            fullWidth>
            <DialogTitle sx={{ fontWeight: 700, fontSize: '20px' }}>
                {mode === 'reschedule'
                    ? 'Reprogrammer l&apos;entretien (Premium)'
                    : 'Programmer un entretien (Premium)'}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 2 }}>
                    {bookingError && (
                        <Typography color="error" variant="body2">
                            {bookingError}
                        </Typography>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}>
                        {mode === 'reschedule' && fetchBookingLoading ? (
                            <Box display="flex" justifyContent="center" py={4}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : servicesLoading ? (
                            <Box display="flex" justifyContent="center" py={4}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <FormControl fullWidth size="small">
                                <FormLabel>Service</FormLabel>
                                <Select
                                    value={selectedServiceId}
                                    label="Service"
                                    onChange={e => handleServiceChange(e.target.value)}
                                    disabled={mode === 'reschedule' || isLoading}>
                                    {services.map((s: any) => (
                                        <MenuItem key={s.id} value={s.id}>
                                            {s.name} ({s.duration})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        <FormControl fullWidth size="small">
                            <FormLabel>Interlocuteur</FormLabel>
                            <Select
                                value={selectedStaffId}
                                disabled={mode === 'reschedule' || !selectedServiceId || isLoading}
                                label="Interlocuteur"
                                onChange={e => setSelectedStaffId(e.target.value)}>
                                {staffList.map((staff: any) => (
                                    <MenuItem key={staff.id} value={staff.id}>
                                        {staff.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 320px) 1fr', gap: 3 }}>
                        <Box>
                            <FormLabel>Date</FormLabel>
                            <DatePicker
                                value={selectedDate}
                                onChange={handleDateChange}
                                disablePast
                                format="DD/MM/YYYY"
                                label="Choisir une date"
                                disabled={!selectedServiceId || !selectedStaffId || isLoading}
                                shouldDisableDate={day => day.day() === 0 || day.day() === 6}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                        </Box>

                        <Box>
                            <FormLabel>
                                {selectedDate
                                    ? `Créneaux — ${selectedDate.format('dddd DD MMMM')}`
                                    : 'Créneaux disponibles'}
                            </FormLabel>

                            {availLoading ? (
                                <Box display="flex" justifyContent="center" py={1}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : !selectedDate ? (
                                <Typography color="text.secondary" variant="body2" sx={{ py: 1.5 }}>
                                    Sélectionnez une date pour voir les créneaux disponibles.
                                </Typography>
                            ) : !hasSlots ? (
                                <Typography color="text.secondary" variant="body2" sx={{ py: 1.5 }}>
                                    Aucun créneau disponible pour cette date.
                                </Typography>
                            ) : (
                                <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
                                    <Stack spacing={2}>
                                        {groupedSlots.map(group => (
                                            <Box key={group.label}>
                                                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                    {group.label}
                                                </Typography>
                                                <SlotsGrid sx={{ mt: 1 }}>
                                                    {group.slots.map(slot => (
                                                        <SlotButton
                                                            key={slot}
                                                            value={slot}
                                                            isSelected={selectedSlot === slot}
                                                            onChange={() => setSelectedSlot(slot)}>
                                                            {parseSlotTime(slot)}
                                                        </SlotButton>
                                                    ))}
                                                </SlotsGrid>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                <Button variant="text" color="inherit" size="large" onClick={onClose} disabled={isLoading}>
                    Annuler
                </Button>
                <Button
                    variant="contained"
                    size="large"
                    onClick={handleConfirm}
                    disabled={
                        !selectedSlot || !selectedStaffId || isLoading || (mode === 'reschedule' && !existingBookingId)
                    }
                    sx={{ minWidth: 160 }}>
                    {isLoading ? (
                        <CircularProgress size={18} />
                    ) : mode === 'reschedule' ? (
                        'Confirmer la reprogrammation'
                    ) : (
                        'Confirmer la réservation'
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PremiumBookingDialog;
